import { SuperCalendar } from '@superops/ui-design-system';
import React, { useContext, useEffect, useState } from 'react';
import SchedulingContext from '../Contexts/SchedulingContext';
import moment from 'moment';
import 'moment/locale/en-gb';
import { momentLocalizer } from 'react-big-calendar';
import {
  EventEmitter,
  getDateTimeFormat,
  getCurrentUserHook
} from '@nile/components/src';
import EventComponent from '../Components/EventComponent';
import EventWrapper from '../Components/EventWrapper';
import { cloneDeep, isEmpty } from 'lodash';
import {
  AccountClient,
  GET_CALENDAR_VIEW_EVENTS,
  GET_DISPATCHER_CALENDAR_EVENTS,
  GET_TECHS_BY_CRITERIA,
  TicketClient,
  useCachedQuery,
  useQuery
} from '@nile/graphql/src';
import ResourceHeader from '../Components/CalendarHeaders/ResourceHeader';
import {
  getDayDetails,
  scrollToCurrentTime,
  getFormattedStartAndEndTime
} from '../helpers';

let broadcastChannel;
if (BroadcastChannel) {
  broadcastChannel = new BroadcastChannel('SuperShare');
}

const sendWSEvent = (event, userId) => {
  const postMessageData = {
    context: 'pageUpdate',
    payload: {
      type: 'CLIENT_EVENT',
      page: {
        type: 'DETAIL',
        entity: 'UserCalendarInfo',
        recordId: userId
      },
      event: event,
      user: {
        userId: userId
      },
      epochMillis: new Date().getTime().toString()
    }
  };

  broadcastChannel.postMessage(
    JSON.stringify({
      name: 'wsdata',
      data: postMessageData
    })
  );
};

const DispatcherCalendar = () => {
  const schedulingContext = useContext(SchedulingContext);
  const [events, setEvents] = useState([]);
  const [slaEvents, setSlaEvents] = useState([]);

  const DEFAULT_RESOURCES = !isEmpty(
    schedulingContext?.selectedDispatcherTechnicians
  )
    ? schedulingContext?.selectedDispatcherTechnicians.map((el) => ({
        ...el,
        events: events
      }))
    : [];
  const [resources, setResources] = useState(DEFAULT_RESOURCES);

  const { loading: techDataLoading, data: techData } = useQuery(
    GET_TECHS_BY_CRITERIA,
    {
      fetchPolicy: 'network-only',
      client: AccountClient,
      variables: {
        input: {
          listInfo: {
            sort: {
              attribute: 'name',
              order: 'ASC'
            }
          }
        }
      },
      onCompleted(res) {
        const currentTechs = DEFAULT_RESOURCES;
        const allTechnicians = res?.getTechnicianList?.userList || [];
        const updatedResourcesList = [];
        currentTechs.forEach((el) => {
          const userId = el?.userId;
          const isIntegrated = allTechnicians.find(
            (tech) => tech?.userId === userId && tech?.calendarIntegrated
          );
          if (isIntegrated) {
            updatedResourcesList.push(el);
          }
        });
        schedulingContext?.setSelectedDispatcherTechnicians(
          updatedResourcesList
        );
        schedulingContext?.saveDispatcherTechniciansToPersonalisation(
          updatedResourcesList
        );
      }
    }
  );

  useEffect(() => {
    const updatedResources = schedulingContext?.selectedDispatcherTechnicians.map(
      (el) => ({
        ...el,
        events: events
      })
    );
    setResources(updatedResources);
  }, [schedulingContext?.selectedDispatcherTechnicians]);

  const is24hours = getDateTimeFormat()?.timeType === '24Hours';

  if (is24hours) {
    moment.locale('en-GB');
  } else {
    moment.locale('ko', {
      week: {
        dow: 1,
        doy: 1
      }
    });
  }

  const localizer = momentLocalizer(moment); // End calendar settings

  const getCalendarVariables = () => {
    const selectedDate = schedulingContext?.selectedDate;
    const dayDetails = getDayDetails(selectedDate);
    const selectedTechnicians = schedulingContext?.selectedDispatcherTechnicians.map(
      (el) => el?.userId
    );

    const { startTime, endTime } = dayDetails;

    const SLA_CONDITION_INPUT = {
      joinOperator: 'AND',
      operands: [
        {
          attribute: 'eventType',
          operator: 'is',
          value: 'SLA_EVENT'
        },
        {
          attribute: 'workItem.ticket.technician.userId',
          operator: 'includes',
          value: selectedTechnicians
        }
      ]
    };

    const filterConditions = schedulingContext?.filterConditions;

    const firstOperands = [
      {
        joinOperator: 'AND',
        operands: [
          {
            attribute: 'eventType',
            operator: 'is',
            value: 'APPOINTMENT'
          },
          {
            attribute: 'appointment.attendees.user.userId',
            operator: 'includes',
            value: selectedTechnicians
          }
        ]
      }
    ];

    const mainConditions = [
      {
        joinOperator: 'OR',
        operands: [
          {
            attribute: 'startTime',
            operator: 'between',
            value: [startTime, endTime]
          },
          {
            attribute: 'endTime',
            operator: 'between',
            value: [startTime, endTime]
          },
          {
            joinOperator: 'AND',
            operands: [
              {
                attribute: 'startTime',
                operator: 'before',
                value: startTime
              },
              {
                attribute: 'endTime',
                operator: 'after',
                value: endTime
              }
            ]
          }
        ]
      }
    ];

    if (filterConditions) {
      mainConditions.push(filterConditions);
    }

    if (schedulingContext?.showSla) {
      firstOperands.push(SLA_CONDITION_INPUT);
    }

    return {
      input: {
        groupBy: {
          attribute: 'appointment.attendees.user.userId'
        },
        condition: {
          joinOperator: 'AND',
          operands: [
            {
              joinOperator: 'OR',
              operands: firstOperands
            },
            ...mainConditions
          ]
        }
      }
    };
  };

  const selectedDate = schedulingContext?.selectedDate;

  const {
    loading,
    data: calendarEvents,
    refetch: refetchCalendarView
  } = useQuery(GET_DISPATCHER_CALENDAR_EVENTS, {
    client: TicketClient,
    variables: getCalendarVariables(),
    fetchPolicy: 'network-only',
    onCompleted(res) {
      if (res?.getCalendarView) {
        const eventsFromRespose = res?.getCalendarView;
        const formattedSlaEvents = [];
        const formattedEvents = [];
        eventsFromRespose.forEach((el) => {
          const userId = el?.header?.value?.userId;
          const events = el?.events || [];
          events?.forEach((event) => {
            const { start, end } = getFormattedStartAndEndTime(
              event?.startTime,
              event?.endTime,
              selectedDate
            );
            if (event?.eventType === 'SLA_EVENT') {
              formattedSlaEvents.push({
                start: start,
                end: end,
                ...event,
                userId: userId,
                resourceId: userId
              });
            } else {
              formattedEvents.push({
                start: start,
                end: end,
                ...event,
                userId: userId,
                resourceId: userId
              });
            }
          });
        });

        EventEmitter.dispatch('refreshNavigationSchedulingWidget');
        setSlaEvents(formattedSlaEvents);
        setEvents(formattedEvents);
      }
    }
  });

  const onCancelNewEvent = () => {
    const updatedEvents = cloneDeep(events).filter(
      (el) => el?.eventType !== 'NEW_EVENT'
    );
    setEvents(updatedEvents);
  };

  const addNewEvent = (date) => {
    const prevEvents = cloneDeep(events).filter(
      (el) => el?.eventType !== 'NEW_EVENT'
    );

    const selectedTech = [];
    if (!isEmpty(date?.resourceId)) {
      schedulingContext?.selectedDispatcherTechnicians.forEach((el) => {
        if (el?.userId === date?.resourceId && isEmpty(selectedTech)) {
          selectedTech.push({
            ...el,
            value: el?.name,
            label: el?.name,
            attendeeType: 'ORGANIZER',
            acceptanceStatus: 'ORGANIZER'
          });
        }
      });
    }

    const updatedEvents = [
      ...prevEvents,
      {
        start: date?.start,
        end: date?.end,
        startDate: date?.start,
        endDate: date?.end,
        eventType: 'NEW_EVENT',
        onCancel: onCancelNewEvent,
        events: events,
        setEvents: setEvents,
        userId: date?.resourceId,
        resourceId: date?.resourceId,
        attendees: selectedTech
      }
    ];
    setEvents(updatedEvents);
  };

  broadcastChannel.onmessage = (event) => {
    const eventData = JSON.parse(event.data);
    const { context, payload } = eventData;
    if (
      payload?.type === 'SERVER_EVENT' &&
      payload?.page?.entity === 'CalendarEvent' &&
      payload?.page?.type === 'DETAIL' &&
      payload?.event === 'UPDATE'
    ) {
      const appointmentToSync = payload?.data?.appointment;
      if (appointmentToSync) {
        const updatedEvents = [];
        const allEvents = cloneDeep(events);
        allEvents.forEach((event) => {
          if (
            event?.appointment?.appointmentId ===
            appointmentToSync?.appointmentId
          ) {
            const isDeleted = appointmentToSync?.deleted === 'true';
            // If deleted is true we wont push it to the state
            if (!isDeleted) {
              const updatedAppointment = {
                ...event,
                appointment: {
                  ...event?.appointment,
                  syncStatus: appointmentToSync?.syncStatus
                }
              };
              updatedEvents.push(updatedAppointment);
            }
          } else {
            updatedEvents.push(event);
          }
        });
        setEvents(updatedEvents);
        EventEmitter.dispatch('refreshNavigationSchedulingWidget');
      }
    }
  };

  const currentUser = getCurrentUserHook();

  const refreshData = () => {
    refetchCalendarView().then((res) => {
      const eventsFromRespose = res?.data?.getCalendarView;

      if (eventsFromRespose) {
        const formattedSlaEvents = [];
        const formattedEvents = [];
        eventsFromRespose.forEach((el) => {
          const userId = el?.header?.value?.userId;
          const events = el?.events || [];
          events?.forEach((event) => {
            const { start, end } = getFormattedStartAndEndTime(
              event?.startTime,
              event?.endTime,
              selectedDate
            );
            if (event?.eventType === 'SLA_EVENT') {
              formattedSlaEvents.push({
                start: start,
                end: end,
                ...event,
                userId: userId,
                resourceId: userId
              });
            } else {
              formattedEvents.push({
                start: start,
                end: end,
                ...event,
                userId: userId,
                resourceId: userId
              });
            }
          });
        });

        setSlaEvents(formattedSlaEvents);
        setEvents(formattedEvents);
        EventEmitter.dispatch('refreshNavigationSchedulingWidget');
      }
    });
  };

  useEffect(() => {
    scrollToCurrentTime();
  }, [schedulingContext?.selectedDate, loading]);

  useEffect(() => {
    EventEmitter.subscribe('refreshCalendar', refreshData);
    sendWSEvent('REGISTER_PAGE', currentUser?.userId);
    return function cleanup() {
      EventEmitter.unsubscribe('refreshCalendar');
      sendWSEvent('EXIT_PAGE', currentUser?.userId);
    };
  }, []);

  return (
    <div
      id="dispatcher-calendar-view"
      className="fhp w100 scroll dispatcher-calendar"
      key={JSON.stringify(schedulingContext)}
    >
      <SuperCalendar
        defaultDate={schedulingContext?.selectedDate}
        defaultView={'day'}
        events={events}
        backgroundEvents={slaEvents}
        localizer={localizer}
        resourceIdAccessor="userId"
        resources={resources}
        resourceTitleAccessor="name"
        step={60}
        onSelectSlot={addNewEvent}
        components={{
          backgroundEvent: EventComponent,
          event: EventComponent,
          eventWrapper: EventWrapper,
          resourceHeader: ResourceHeader
        }}
        allDayAccessor={'isAllDayEvent'}
        selectable
      />
    </div>
  );
};

export default DispatcherCalendar;
